/*
 * File: CardOptionsWidget.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 28, 2025 at 3:55 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo, useEffect, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Components
import { Typography } from '../Typography';

// Styles
import * as S from './CardOptionsWidget.styles';


/**
 * Component
 */

export const CardOptionsWidget = memo(({
	className, items
}) => {

	// Create state
	const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);

	// Create refs
	const showOptionsDropdownRef = useRef();
	const containerRef = useRef();

	// Toggle navigation visibility
	const toggleNavigation = () => {
		setShowOptionsDropdown(!showOptionsDropdown);
	};

	// Handle actions on ref change
	useEffect(() => {

		// Define click outside function
		function handleClickOutside(event) {
			if (showOptionsDropdownRef.current && containerRef.current && !containerRef.current.contains(event.target)) {
				setShowOptionsDropdown(false);
			}
		}

		// Bind the event listener
		document.addEventListener('mousedown', handleClickOutside);

		// Return effect
		return () => { // Unbind the event listener on clean up
			document.removeEventListener('mousedown', handleClickOutside);
		};

	}, [containerRef]);

	// Handle actions on dropdown state
	useEffect(() => {

		// Update ref
		showOptionsDropdownRef.current = showOptionsDropdown;

	}, [showOptionsDropdown]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<S.DropdownContainer className={className} ref={containerRef}>

			{/* Dropdown Toggle */}
			<S.DropdownToggle size={1} icon={['far', 'ellipsis']} onClick={toggleNavigation} />

			{/* Dropdown */}
			<S.OptionsDropdown className={showOptionsDropdown ? 'animate show' : 'animate'}>
				{items.map((item) => (
					<S.NavigationItem className="animate" onClick={() => { item.action(); toggleNavigation(); }} key={item.text}>
						<S.IconContainer>
							<FontAwesomeIcon icon={item.icon} />
						</S.IconContainer>
						<Typography variation="2" tag="p" weight="regular">{item.text}</Typography>
					</S.NavigationItem>
				))}
			</S.OptionsDropdown>
		</S.DropdownContainer>
	);
});


/**
 * Configuration
 */

CardOptionsWidget.displayName = 'CardOptionsWidget';
CardOptionsWidget.propTypes = {
	className: PropTypes.string,
	items: PropTypes.arrayOf(PropTypes.shape()),
};
CardOptionsWidget.defaultProps = {
	className: null,
	items: []
};
